import { useEffect, useMemo, useState } from 'react';
import { Wordmark } from '../components/Wordmark.jsx';
import { APP_DOWNLOAD_URL } from '../config/index.js';
import { SwipeCard } from '../features/discover/SwipeCard.jsx';
import { GroupJoinPrompt } from '../features/group/GroupJoinPrompt.jsx';
import { GroupDeckSetup } from '../features/group/GroupDeckSetup.jsx';
import { GroupResultsSheet } from '../features/group/GroupResultsSheet.jsx';
import { GroupStatusBar } from '../features/group/GroupStatusBar.jsx';
import { manualGroupDeck, randomGroupDeck } from '../features/group/deckSetup.js';
import { deckSignature, firstUnvotedDeckIndex } from '../features/group/deckProgress.js';
import { memberIdForAppIdentity, unclaimedGuests } from '../features/group/guestList.js';
import { useGroupDeckRestaurants } from '../features/group/useGroupDeckRestaurants.js';
import { peekSession } from '../features/group/sync/index.js';
import { useGroupSession } from '../features/group/useGroupSession.js';
import { useCatalogue } from '../hooks/useCatalogue.js';
import { getAppIdentity, getDeviceId, getGuestName } from '../lib/identity.js';
import { postDeckState } from '../lib/messagesBridge.js';
import { color, font, radius } from '../theme/tokens.js';

/** Cards rendered at once. Only the front one is interactive. */
const VISIBLE_CARDS = 3;

/**
 * Voting in a group, from a browser.
 *
 * The whole reason the web build exists: someone gets a link, opens it on
 * whatever phone they have, and votes without installing anything. Everything
 * else — saving places, an account, the full deck — is in the iOS app, and the
 * footer says so.
 *
 * Deliberately no swipe limit. A group of four each capped at ten swipes would
 * leave the decision half-made, which would break the one feature this page is
 * for.
 *
 * The same screen runs inside the iMessage extension, which hosts it in a web
 * view rather than rebuilding the deck in Swift. That costs two props and a
 * status message; it saves maintaining a second deck. See
 * mobile/targets/imessage and client/src/lib/messagesBridge.js.
 *
 * @param {object} props
 * @param {string} props.code  Session code from the invite link.
 * @param {boolean} [props.embedded]  True inside a message sheet: no header, no footer.
 * @param {boolean} [props.hosting]  True when this page should create the session.
 * @param {{id: string, name: string}[]} [props.invites]  Set only for the person starting the group.
 * @param {'random'|'pick'} [props.setupMode]  How an iMessage host wants to build the ballot.
 */
export function GroupVoteScreen({
  code,
  embedded = false,
  hosting = false,
  invites = [],
  setupMode = 'random',
}) {
  const {
    catalogue,
    status: catalogueStatus,
    coords,
  } = useCatalogue({
    // Messages' WKWebView can leave browser geolocation in `idle` forever.
    // Embedded sessions should load immediately with the shared catalogue.
    skipLocation: embedded,
  });
  const group = useGroupSession(catalogue);
  // The host's deck, loaded by id when it isn't among the restaurants near this
  // guest — everyone in the group swipes the same cards.
  const { restaurants: deckRestaurants, loading: groupDeckLoading } = useGroupDeckRestaurants(
    group.deckSelection,
    catalogue
  );
  const resolveRestaurants = group.resolveRestaurants;
  useEffect(() => resolveRestaurants(deckRestaurants), [deckRestaurants, resolveRestaurants]);
  const groupCatalogue = useMemo(() => {
    if (group.deckSelection?.mode !== 'manual') return catalogue;

    const byId = new Map(deckRestaurants.map((restaurant) => [restaurant.id, restaurant]));
    return group.deckSelection.restaurantIds.map((id) => byId.get(id)).filter(Boolean);
  }, [catalogue, deckRestaurants, group.deckSelection]);

  // A host flag arrives from the extension even when there is no roster yet.
  // Older links without the flag still create when they carry invites.
  const shouldCreate = hosting || invites.length > 0;

  const [joined, setJoined] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [hostError, setHostError] = useState(null);
  const [hostAttempt, setHostAttempt] = useState(0);
  const [hostIdentityMissing, setHostIdentityMissing] = useState(false);
  const [guests, setGuests] = useState([]);
  const [deckIndex, setDeckIndex] = useState(0);
  const [flippedId, setFlippedId] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [hostDeck, setHostDeck] = useState(null);
  // The invite was opened after the group finished: nothing to join, only an
  // answer to show.
  const [endedOnArrival, setEndedOnArrival] = useState(false);

  const visible = groupCatalogue.slice(deckIndex, deckIndex + VISIBLE_CARDS);
  const remaining = groupCatalogue.length - deckIndex;
  const groupDeckSignature = useMemo(() => deckSignature(groupCatalogue), [groupCatalogue]);
  const myPickCount = Object.values(group.myVotes).filter((vote) => vote === 'right').length;

  // A random deck is drawn once by the host and then persisted to the session.
  // Manual setup leaves this null until the host confirms the picker below.
  useEffect(() => {
    if (
      !shouldCreate ||
      setupMode === 'pick' ||
      hostDeck !== null ||
      catalogueStatus === 'loading' ||
      catalogue.length === 0
    ) {
      return;
    }
    setHostDeck(randomGroupDeck(catalogue));
  }, [catalogue, catalogueStatus, hostDeck, setupMode, shouldCreate]);

  // A fresh deck each time the catalogue changes underneath (location resolving
  // turns the placeholder list into real nearby venues). Re-entered sessions
  // resume from the first card this member has not already swiped.
  useEffect(() => {
    setDeckIndex(firstUnvotedDeckIndex(groupCatalogue, joined ? group.myVotes : null));
    // The signature is the dependency that matters; the catalogue array can be
    // rebuilt with the same ids while the embedded deck is being reopened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupDeckSignature, joined, group.memberId, group.myVotes]);

  // Votes are live as they are swiped. Finishing the shared ballot is recorded
  // automatically only so the session knows when everyone is done.
  const finishVoting = group.submitVotes;
  useEffect(() => {
    if (
      !joined ||
      group.hasSubmitted ||
      group.isEnded ||
      groupCatalogue.length === 0 ||
      !groupCatalogue.every((restaurant) => group.myVotes[restaurant.id] != null)
    ) {
      return;
    }
    finishVoting().catch(() => {});
  }, [finishVoting, group.hasSubmitted, group.isEnded, group.myVotes, groupCatalogue, joined]);

  const connectGroup = group.connect;

  /**
   * Reads the session before joining it, to find out whether it is expecting
   * particular people.
   *
   * Has to happen before anyone is a member, so it cannot come from the live
   * subscription. A failure is deliberately silent: the join below raises the
   * same problem with a message already written for it, and the prompt still
   * works with an empty guest list.
   */
  useEffect(() => {
    if (shouldCreate) return undefined;

    let active = true;
    peekSession(code)
      .then((state) => {
        if (!active) return;
        if (state?.ended) {
          setEndedOnArrival(true);
          group.observe(code).catch(() => {});
          return;
        }
        const currentMemberId = getDeviceId();
        const appIdentity = getAppIdentity();
        if (appIdentity) {
          connectGroup(code, appIdentity.name, {
            as: memberIdForAppIdentity(state?.invites, state?.members, appIdentity),
          })
            .then(() => {
              if (active) setJoined(true);
            })
            .catch(() => {
              if (active) setGuests(unclaimedGuests(state?.invites, state?.members));
            });
          return;
        }

        const existingMember = state?.members?.[currentMemberId];
        if (existingMember) {
          connectGroup(code, existingMember.name || getGuestName() || 'Guest', {
            as: currentMemberId,
          })
            .then(() => {
              if (active) setJoined(true);
            })
            .catch(() => {
              if (active) setGuests(unclaimedGuests(state?.invites, state?.members));
            });
          return;
        }

        setGuests(unclaimedGuests(state?.invites, state?.members));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // `connectGroup` and `group.observe` are stable; the peek belongs to the
    // code, not the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, shouldCreate, connectGroup]);

  // Everyone lands on the answer when the group ends, whoever ended it.
  const isEnded = group.isEnded;
  useEffect(() => {
    if (isEnded) setShowResults(true);
  }, [isEnded]);

  // Finishing the ballot goes directly to the live tally. The votes were
  // already written as each card was swiped; this is navigation, not submit.
  useEffect(() => {
    if (group.hasSubmitted) setShowResults(true);
  }, [group.hasSubmitted]);

  /**
   * Opens the session for the person who started it from a message thread.
   *
   * No prompt: they already tapped "start a group" one screen ago, and the app
   * has told us their name. Writing the roster in as invites is the part that
   * matters — it is what lets everyone who taps the bubble claim the name the
   * host knows them by, which is the difference between an evening filed
   * against the group they have eaten with three times and a fourth group that
   * has met once.
   */
  useEffect(() => {
    // Waits for the host to choose or draw a deck so the same restaurant ids
    // are in the session before any participant begins swiping.
    if (
      !shouldCreate ||
      joined ||
      hostError !== null ||
      catalogueStatus === 'loading' ||
      hostDeck === null
    ) {
      return undefined;
    }

    const hostName = getGuestName().trim();
    if (!hostName) {
      setHostIdentityMissing(true);
      return undefined;
    }

    let active = true;
    connectGroup(code, hostName, { create: true, invites, deck: hostDeck })
      .then(() => {
        if (active) setJoined(true);
      })
      .catch(() => {
        // This page is the creator. Falling through to the ordinary join
        // prompt asks it to join a session that creation just failed to write,
        // producing a second, misleading "invalid invite" error. Keep the
        // host in a retryable creation state instead.
        if (active) setHostError('The group could not be opened. Check your connection and retry.');
      });
    return () => {
      active = false;
    };
  }, [
    code,
    connectGroup,
    shouldCreate,
    invites,
    joined,
    catalogueStatus,
    hostDeck,
    hostError,
    hostAttempt,
  ]);

  /**
   * Reports the session to the extension hosting this page.
   *
   * Only this side knows any of it — it holds the subscription. The bubble
   * carrying "3 people voting · Fiorella ahead" back up the thread is most of
   * what makes the message worth tapping twice, and the roster underneath it is
   * what lets the app file this evening as a meeting the next time it launches.
   * A no-op in a browser.
   */
  const { memberCount, memberId, members, topPick } = group;
  const pickId = topPick?.restaurant?.id ?? null;
  const pickName = topPick?.restaurant?.name ?? null;
  useEffect(() => {
    if (!embedded || (!joined && !endedOnArrival)) return;

    postDeckState({
      memberCount,
      // A group is the other people — the same definition group history uses,
      // so the roster that arrives there needs no translation.
      members: Object.entries(members)
        .filter(([id]) => id !== memberId)
        .map(([id, member]) => ({ id, name: member.name || 'Guest' })),
      pick: pickId !== null && pickName ? { id: pickId, name: pickName } : null,
      ended: group.isEnded,
    });
  }, [
    embedded,
    joined,
    endedOnArrival,
    memberCount,
    memberId,
    members,
    pickId,
    pickName,
    group.isEnded,
  ]);

  async function handleJoin(name, as) {
    await group.connect(code, name, { as });
    setJoined(true);
  }

  function handleSwipe(direction, restaurant) {
    setDeckIndex((index) => index + 1);
    setFlippedId(null);
    group.recordVote(restaurant, direction);
  }

  if (declined) return <DeclinedNotice code={code} />;

  return (
    <div style={embedded ? styles.embeddedRoot : styles.root}>
      {/* Both of these are dropped inside a message sheet. The wordmark is
          redundant — the bubble that opened this said Crave — and the footer's
          "get the app" has nowhere to open from inside Messages. What is left
          is the deck and the status bar, which is all the room is good for. */}
      {!embedded && (
        <header style={styles.header}>
          <Wordmark height={20} />
          {joined && (
            <span style={styles.headerNote}>
              {group.memberCount} {group.memberCount === 1 ? 'person' : 'people'}
            </span>
          )}
        </header>
      )}

      {joined && group.isActive && !group.isEnded && (
        <GroupStatusBar
          code={group.code}
          memberCount={group.memberCount}
          submittedCount={group.submittedCount}
          topPick={group.topPick}
          onShowResults={() => setShowResults(true)}
          onLeave={
            // Whoever started the group ends it instead of leaving it.
            shouldCreate
              ? null
              : () => {
                  // Out of the roster too, or the group would wait forever for
                  // someone who is gone to share.
                  group.leave().catch(() => {});
                  setDeclined(true);
                }
          }
          compact={embedded}
        />
      )}

      <main style={embedded ? styles.embeddedDeck : styles.deck}>
        {hostIdentityMissing ? (
          <div style={styles.centered}>
            <p style={styles.centeredTitle}>Set up your name in Crave</p>
            <p style={styles.centeredText}>
              Open the Crave app, set your name in Profile or sign in, then start the group again.
            </p>
          </div>
        ) : hostError ? (
          <div style={styles.centered}>
            <p style={styles.centeredTitle}>Couldn&apos;t open the group</p>
            <p style={styles.centeredText}>{hostError}</p>
            <button
              type="button"
              style={styles.linkButton}
              onClick={() => {
                setHostError(null);
                setHostAttempt((attempt) => attempt + 1);
              }}
            >
              Retry
            </button>
          </div>
        ) : shouldCreate && setupMode === 'pick' && !joined && hostDeck === null ? (
          <GroupDeckSetup
            restaurants={catalogue}
            loading={catalogueStatus === 'loading'}
            coords={coords ?? catalogue[0]?.coords ?? null}
            onStart={(selected) => setHostDeck(manualGroupDeck(selected))}
          />
        ) : group.isEnded ? (
          <div style={styles.centered}>
            <p style={styles.centeredTitle}>
              {group.topPick
                ? `The group picked ${group.topPick.restaurant.name}`
                : 'This group has ended'}
            </p>
            <button type="button" style={styles.linkButton} onClick={() => setShowResults(true)}>
              See the results
            </button>
          </div>
        ) : group.hasSubmitted ? (
          <div style={styles.centered}>
            <p style={styles.centeredTitle}>You&apos;ve seen every place</p>
            <p style={styles.centeredText}>
              {`You picked ${myPickCount} ${myPickCount === 1 ? 'place' : 'places'}. `}
              {group.memberCount - group.submittedCount > 0
                ? `Waiting on ${group.memberCount - group.submittedCount} more.`
                : 'Everyone is finished.'}
            </p>
            <button type="button" style={styles.linkButton} onClick={() => setShowResults(true)}>
              View your picks and results
            </button>
          </div>
        ) : catalogueStatus === 'loading' || groupDeckLoading ? (
          <div style={styles.centered}>
            <span className="spinner" style={{ width: 22, height: 22 }} />
            <p style={styles.centeredText}>Finding restaurants near you…</p>
          </div>
        ) : remaining > 0 ? (
          visible.map((restaurant, position) => (
            <SwipeCard
              key={restaurant.id}
              restaurant={restaurant}
              stackIndex={position}
              tier={null}
              origin={coords}
              onSwipe={handleSwipe}
              flipped={flippedId === restaurant.id}
              onFlippedChange={(next) => setFlippedId(next ? restaurant.id : null)}
              groupVotes={group.voteCounts[restaurant.id] ?? 0}
              groupSize={group.memberCount}
              compact={embedded}
            />
          ))
        ) : (
          <div style={styles.centered}>
            <p style={styles.centeredTitle}>That&apos;s everywhere nearby</p>
            <p style={styles.centeredText}>Your votes have been counted.</p>
            <button type="button" style={styles.linkButton} onClick={() => setShowResults(true)}>
              See what the group picked
            </button>
          </div>
        )}
      </main>

      {!embedded && (
        <footer style={styles.footer}>
          Voting as a guest.{' '}
          {APP_DOWNLOAD_URL ? (
            <a href={APP_DOWNLOAD_URL} style={styles.footerLink}>
              Get the app
            </a>
          ) : (
            <span style={styles.footerLink}>Open Crave on iPhone</span>
          )}{' '}
          to save places and start your own groups.
        </footer>
      )}

      {!joined && !declined && !endedOnArrival && !hostIdentityMissing && !shouldCreate && (
        <GroupJoinPrompt
          code={code}
          guests={guests}
          onJoin={handleJoin}
          onDecline={() => setDeclined(true)}
        />
      )}

      {showResults && (
        <GroupResultsSheet
          leaderboard={group.leaderboard}
          memberCount={group.memberCount}
          submittedCount={group.submittedCount}
          myVotes={group.myVotes}
          isEnded={group.isEnded}
          onEnd={joined && shouldCreate ? group.endGroup : null}
          onClose={() => setShowResults(false)}
        />
      )}
    </div>
  );
}

/** Shown when someone backs out of the invite, so the page isn't a dead end. */
function DeclinedNotice({ code }) {
  return (
    <div style={styles.root}>
      <div style={styles.centered}>
        <Wordmark height={26} />
        <p style={{ ...styles.centeredTitle, marginTop: 18 }}>You&apos;ve left {code}</p>
        <p style={styles.centeredText}>
          Reopen the invite link to rejoin, or ask whoever started it for the code.
        </p>
        <a href="/" style={styles.linkButton}>
          About Crave
        </a>
      </div>
    </div>
  );
}

const styles = {
  root: {
    minHeight: '100vh',
    maxWidth: 460,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    background: color.surface,
  },
  embeddedRoot: {
    position: 'fixed',
    inset: 0,
    height: '100dvh',
    width: '100vw',
    maxWidth: 'none',
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: color.surface,
  },
  header: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px 10px',
  },
  headerNote: { fontSize: 12, color: color.inkFaint },
  deck: { flex: 1, position: 'relative', minHeight: 0, margin: '4px 12px 0' },
  embeddedDeck: {
    flex: '0 1 auto',
    position: 'relative',
    minHeight: 0,
    width: 'min(94vw, 390px)',
    height: 'min(560px, calc(100dvh - 52px))',
    margin: '10px auto 16px',
    overflow: 'visible',
  },
  centered: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: '0 32px',
    textAlign: 'center',
  },
  centeredTitle: { margin: 0, fontSize: 17, fontWeight: font.weight.bold, color: color.ink },
  centeredText: { margin: 0, fontSize: 13.5, lineHeight: 1.6, color: color.inkFaint },
  shareButton: {
    marginTop: 6,
    padding: '11px 28px',
    borderRadius: radius.pill,
    border: 'none',
    background: color.primary,
    color: color.inkInverse,
    fontSize: 14,
    fontWeight: font.weight.bold,
    cursor: 'pointer',
  },
  linkButton: {
    marginTop: 6,
    padding: '9px 18px',
    borderRadius: radius.pill,
    border: `1px solid ${color.border}`,
    background: 'none',
    color: color.inkMuted,
    fontSize: 13,
    textDecoration: 'none',
    cursor: 'pointer',
  },
  footer: {
    flexShrink: 0,
    padding: '12px 20px 20px',
    textAlign: 'center',
    fontSize: 11.5,
    lineHeight: 1.6,
    color: color.inkFaint,
  },
  footerLink: { color: color.primary, fontWeight: font.weight.semibold, textDecoration: 'none' },
};
